import React, { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import {
    RErrorText,
    RFieldGroup,
    RModalFooter,
    RPillButton,
    RStack,
    RText,
    RTextInput,
} from 'recruit/components';
import { createSetupIntent, savePaymentMethod } from './api';
import { readPaymentMethodId } from './stripeHelpers';
import { PaymentMethod } from './types';

interface AddBankAccountFormProps {
    onSuccess: (method: PaymentMethod) => void;
    onCancel?: () => void;
    cancelLabel?: string;
}

interface BankConfirmDetails {
    last4?: string;
    routing?: string;
}

export function AddBankAccountForm({ onSuccess, onCancel, cancelLabel = 'Cancel' }: AddBankAccountFormProps) {
    const stripe = useStripe();
    const [accountHolderName, setAccountHolderName] = useState('');
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [confirmDetails, setConfirmDetails] = useState<BankConfirmDetails | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inConfirmation = confirmDetails !== null;

    async function startBankCollection() {
        if (!stripe) return;
        setSubmitting(true);
        setError(null);
        try {
            const { client_secret } = await createSetupIntent('us_bank_account');
            setClientSecret(client_secret);

            const result = await stripe.collectBankAccountForSetup({
                clientSecret: client_secret,
                params: {
                    payment_method_type: 'us_bank_account',
                    payment_method_data: {
                        billing_details: { name: accountHolderName || undefined },
                    },
                },
                expand: ['payment_method'],
            });

            if (result.error) {
                setError(result.error.message ?? 'Could not connect bank account.');
                setClientSecret(null);
                setSubmitting(false);
                return;
            }

            const setupIntent = result.setupIntent;
            if (setupIntent.status === 'requires_payment_method') {
                setClientSecret(null);
                setSubmitting(false);
                return;
            }

            if (setupIntent.status === 'requires_confirmation' && typeof setupIntent.payment_method !== 'string') {
                const us = setupIntent.payment_method?.us_bank_account;
                setConfirmDetails({ last4: us?.last4, routing: us?.routing_number ?? undefined });
                setSubmitting(false);
                return;
            }

            setSubmitting(false);
        } catch {
            setError('Could not connect bank account. Please try again.');
            setClientSecret(null);
            setSubmitting(false);
        }
    }

    async function confirmBankAccount() {
        if (!stripe || !clientSecret) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await stripe.confirmUsBankAccountSetup(clientSecret);
            if (result.error) {
                setError(result.error.message ?? 'Could not confirm bank account.');
                setSubmitting(false);
                return;
            }
            const setupIntent = result.setupIntent;
            const paymentMethodId = readPaymentMethodId(result);
            if (!paymentMethodId) {
                setError('Stripe did not return a payment method id.');
                setSubmitting(false);
                return;
            }
            const saved = await savePaymentMethod(paymentMethodId, setupIntent.status);
            setSubmitting(false);
            onSuccess(saved);
        } catch {
            setError('Could not save bank account. Please try again.');
            setSubmitting(false);
        }
    }

    return (
        <RStack gap="md">
            {!inConfirmation && (
                <RFieldGroup label="Account Holder Name">
                    <RTextInput
                        value={accountHolderName}
                        onChange={setAccountHolderName}
                        placeholder="Name on account"
                        fullWidth
                    />
                </RFieldGroup>
            )}
            {inConfirmation && confirmDetails && (
                <RStack gap="sm">
                    <RText>
                        <strong>Account ending in:</strong> {confirmDetails.last4 ?? '????'}
                    </RText>
                    {confirmDetails.routing && (
                        <RText>
                            <strong>Routing number:</strong> {confirmDetails.routing}
                        </RText>
                    )}
                    <RText tone="secondary" size="sm">
                        Confirm to save this bank account.
                    </RText>
                </RStack>
            )}
            {error && <RErrorText>{error}</RErrorText>}
            {!stripe && <RText tone="secondary">Loading Stripe…</RText>}
            <RModalFooter>
                {onCancel && (
                    <RPillButton variant="secondary" onClick={onCancel} disabled={submitting}>
                        {cancelLabel}
                    </RPillButton>
                )}
                <RPillButton
                    variant="primary"
                    onClick={inConfirmation ? confirmBankAccount : startBankCollection}
                    disabled={!stripe || submitting}
                >
                    {submitting
                        ? 'Saving…'
                        : inConfirmation
                          ? 'Confirm Bank Account'
                          : 'Connect Bank Account'}
                </RPillButton>
            </RModalFooter>
        </RStack>
    );
}
